import {
  earnings,
  EarningsFilterRequest,
  EarningsFilterResponse,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';

import { fetchEntities } from '../screener/useScreenerEvents.utils';

const QUERY_KEY_EARNINGS_FILTER = 'QUERY_KEY_EARNINGS_FILTER';
const MAX_PAGE_SIZE = 9999;

export const useEarningsFilter = ({
  body,
  shouldFetchEntities = true,
  pageSize = MAX_PAGE_SIZE,
}: {
  body: EarningsFilterRequest;
  shouldFetchEntities?: boolean;
  pageSize?: number;
}) => {
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: [QUERY_KEY_EARNINGS_FILTER, body, shouldFetchEntities, pageSize],
    queryFn: async ({ signal, pageParam }) => {
      const { path, schema } = earnings.postEarningsFilter;
      const requestBody: EarningsFilterRequest & {
        page: { number: number; size: number };
      } = {
        ...body,
        page: {
          number: pageParam,
          size: pageSize,
        },
      };

      const earningsFilterResponse =
        await wretchRequest<EarningsFilterResponse>(path, {
          method: 'post',
          body: requestBody,
          schema,
          signal,
        });
      const { earnings: earningsData } = earningsFilterResponse;

      const entityTags = earningsData.map(earning => earning.entity_tag);

      const entities = await fetchEntities(
        queryClient,
        shouldFetchEntities ? entityTags : []
      );
      const entitiesMap = new Map(entities.map(entity => [entity.tag, entity]));

      return earningsData.map(earning => {
        const entity = entitiesMap.get(earning.entity_tag);
        return entity ? { ...earning, entity } : earning;
      });
    },
    initialPageParam: 1,
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!body.entities ? body.entities.length > 0 : true,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined;
      } else if (lastPage.length === pageSize) {
        return lastPageParam + 1;
      } else {
        return undefined;
      }
    },
  });
};
